import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Paper, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Brush,
  Legend,
  ReferenceLine,
  ReferenceArea,
} from "recharts";
import { useTimelineInteraction } from "../../interaction/TimelineInteractionContext";
import type { TimeRange } from "../../utils/time";
import {
  formatValueByUnit,
  formatValueForAxis,
  getBytesMagnitude,
  getNumberMagnitude,
  getSecondsMagnitude,
} from "../../utils/formatting";

export interface TimeSeriesPoint {
  ts: Date;
  key?: string;
  value: number | null;
}

export interface TimeSeriesLine {
  id: string;
  label: string;
  color: string;
  points: TimeSeriesPoint[];
}

export interface TimeSeriesLineChartProps {
  series: TimeSeriesLine[];
  strokeWidth?: number;
  yLabel?: string;
  dot?: boolean;
  unit?: string;
  height?: number;
  footer?: ReactNode;
  xRange?: TimeRange;
  onLegendClick?: (instanceId: string) => void;
  /** Extra content below tooltip items (e.g. subsystem status) */
  tooltipExtraContent?: (props: { key: string }) => React.ReactElement | null;
  /** Called when clicking a point that has responseId */
  onPointClick?: (point: { ts: number; key?: string }) => void;
  /** Optional syncId for brushing across charts; omit or use unique id per chart to avoid cross-chart sync issues */
  syncId?: string;
  /** Called when user drag-selects a time range on the chart (rounded to minute boundaries) */
  onRangeSelect?: (range: TimeRange) => void;
}

type RechartsDatum = {
  ts: number;
  points: {
    [key: string]: {
      value: number | null;
      key?: string;
    };
  };
};

type TooltipFormatter = (value: unknown, name: string) => [string, string];

function buildData(series: TimeSeriesLine[]): RechartsDatum[] {
  const allPoints: { ts: number; lineId: string; key?: string; value: number | null }[] = [];
  for (const line of series) {
    for (const p of line.points) {
      allPoints.push({
        ts: p.ts.getTime(),
        key: p.key,
        value: p.value,
        lineId: line.id,
      });
    }
  }

  const byTs = new Map<number, RechartsDatum>();
  for (const point of allPoints) {
    let row = byTs.get(point.ts);
    if (!row) {
      row = { ts: point.ts, points: {} };
      byTs.set(point.ts, row);
    }
    row.points[point.lineId] = { value: point.value, key: point.key };
  }

  return Array.from(byTs.values()).sort((a, b) => a.ts - b.ts);
}

function ensureDomainEndpoints(data: RechartsDatum[], range: TimeRange): RechartsDatum[] {
  const from = range.from.getTime();
  const to = range.to.getTime();

  // Don't mutate caller-owned arrays
  const out = data.slice();

  const hasFrom = out.some((d) => d.ts === from);
  const hasTo = out.some((d) => d.ts === to);

  if (!hasFrom) out.push({ ts: from, points: {} });
  if (!hasTo) out.push({ ts: to, points: {} });

  out.sort((a, b) => a.ts - b.ts);
  return out;
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatDateTime(ts: number): string {
  return new Date(ts).toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/** Floor date to minute boundary */
function floorToMinute(d: Date): Date {
  const t = d.getTime();
  return new Date(Math.floor(t / 60000) * 60000);
}

/** Ceil date to minute boundary */
function ceilToMinute(d: Date): Date {
  const t = d.getTime();
  return new Date(Math.ceil(t / 60000) * 60000);
}



export function TimeSeriesLineChart({
  series,
  yLabel,
  unit,
  height = 220,
  footer,
  xRange,
  onLegendClick,
  tooltipExtraContent,
  onPointClick,
  onRangeSelect,
  dot = false,
  syncId = "time-series-line-chart",
  strokeWidth = 1.8,
}: TimeSeriesLineChartProps) {
  const theme = useTheme();
  const { hoveredRange, setHoveredRange } = useTimelineInteraction();
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragSelect, setDragSelect] = useState<{ startTs: number; endTs: number } | null>(null);

  const pixelToTimestamp = useCallback(
    (clientX: number): number | null => {
      if (!xRange || !containerRef.current) return null;
      const rect = containerRef.current.getBoundingClientRect();
      const plotLeft = rect.left + 68;
      const plotWidth = Math.max(rect.width - 76, 1);
      if (plotWidth <= 0) return null;
      const t = xRange.from.getTime();
      const domain = xRange.to.getTime() - t;
      const frac = (clientX - plotLeft) / plotWidth;
      return t + frac * domain;
    },
    [xRange],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!onRangeSelect || !xRange) return;
      const ts = pixelToTimestamp(e.clientX);
      if (ts != null) {
        setDragSelect({ startTs: ts, endTs: ts });
      }
    },
    [onRangeSelect, xRange, pixelToTimestamp],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!dragSelect) return;
      const ts = pixelToTimestamp(e.clientX);
      if (ts != null) {
        setDragSelect((prev) => (prev ? { ...prev, endTs: ts } : null));
      }
    },
    [dragSelect, pixelToTimestamp],
  );

  const handleMouseUp = useCallback(() => {
    setDragSelect((prev) => {
      if (!prev || !onRangeSelect) return null;
      const [rawStart, rawEnd] =
        prev.startTs <= prev.endTs
          ? [prev.startTs, prev.endTs]
          : [prev.endTs, prev.startTs];
      const from = floorToMinute(new Date(rawStart));
      const to = ceilToMinute(new Date(rawEnd));
      if (to.getTime() - from.getTime() >= 60000) {
        onRangeSelect({ from, to });
      }
      return null;
    });
  }, [onRangeSelect]);

  const handleMouseUpRef = useRef(handleMouseUp);
  handleMouseUpRef.current = handleMouseUp;
  const pixelToTimestampRef = useRef(pixelToTimestamp);
  pixelToTimestampRef.current = pixelToTimestamp;

  useEffect(() => {
    if (!dragSelect) return;
    const onUp = () => handleMouseUpRef.current();
    const onMove = (e: MouseEvent) => {
      if (containerRef.current) {
        const ts = pixelToTimestampRef.current(e.clientX);
        if (ts != null) {
          setDragSelect((prev) =>
            prev ? { ...prev, endTs: ts } : null,
          );
        }
      }
    };
    document.addEventListener("mouseup", onUp);
    document.addEventListener("mousemove", onMove);
    return () => {
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("mousemove", onMove);
    };
  }, [dragSelect]);

  // Find max value to determine magnitude
  const maxValue = useMemo(() => {
    let max = 0;
    for (const line of series) {
      for (const point of line.points) {
        if (point.value != null && point.value > max) {
          max = point.value;
        }
      }
    }
    return max;
  }, [series]);

  // Determine normalization factor and unit based on the highest magnitude
  const { normalizedData, magnitudeInfo, normalizedTooltipValue } = useMemo(() => {
    const coerceNumber = (value: unknown): number | null => {
      if (value == null) return null;
      if (typeof value === "number") return Number.isFinite(value) ? value : null;
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    };

    if (!unit) {
      // No normalization needed for unitless values
      const base = buildData(series);
      return {
        normalizedData: xRange ? ensureDomainEndpoints(base, xRange) : base,
        magnitudeInfo: null,
        normalizedTooltipValue: ((value: unknown, name: string) => {
          const v = coerceNumber(value);
          if (v == null) return ["N/A", name];
          return [formatValueByUnit(v, unit), name];
        }) satisfies TooltipFormatter,
      };
    }

    let factor = 1;
    let magnitudeUnit = "";

    if (unit === "by" || unit === "By") {
      const mag = getBytesMagnitude(maxValue);
      factor = mag.factor;
      magnitudeUnit = mag.unit;
    } else if (unit === "s") {
      const mag = getSecondsMagnitude(maxValue);
      factor = mag.factor;
      magnitudeUnit = mag.unit;
    } else {
      const mag = getNumberMagnitude(maxValue);
      factor = mag.factor;
      magnitudeUnit = mag.unit;
    }

    // Build normalized data
    const allPoints: { ts: number; lineId: string; key?: string; value: number | null }[] = [];
    for (const line of series) {
      for (const p of line.points) {
        allPoints.push({
          ts: p.ts.getTime(),
          key: p.key,
          lineId: line.id,
          value: p.value != null ? p.value / factor : null,
        });
      }
    }

    const byTs = new Map<number, RechartsDatum>();
    for (const point of allPoints) {
      let row = byTs.get(point.ts);
      if (!row) {
        row = { ts: point.ts, points: {} };
        byTs.set(point.ts, row);
      }
      row.points[point.lineId] = { value: point.value, key: point.key };
    }

    const baseData = Array.from(byTs.values()).sort((a, b) => a.ts - b.ts);
    const normalizedData = xRange ? ensureDomainEndpoints(baseData, xRange) : baseData;

    const tooltipFormatter = ((value: unknown, name: string) => {
      const v = coerceNumber(value);
      if (v == null) return ["N/A", name];
      return [`${formatValueForAxis(v)} ${magnitudeUnit}`, name];
    }) satisfies TooltipFormatter;

    return {
      normalizedData,
      magnitudeInfo: { factor, unit: magnitudeUnit },
      normalizedTooltipValue: tooltipFormatter,
    };
  }, [series, unit, maxValue, xRange]);

  const formatYAxisTick = (value: number) => formatValueForAxis(value);

  // Y-axis label includes the unit
  const yAxisLabel = useMemo(() => {
    if (magnitudeInfo) {
      return magnitudeInfo.unit;
    }
    return yLabel || "";
  }, [magnitudeInfo, yLabel]);

  const renderCustomLegend = (props: any) => {
    const { payload } = props;
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: "8px",
          padding: "8px 0",
        }}
      >
        {payload.map((entry: any, index: number) => {
          const line = series.find((s) => s.id === entry.dataKey);
          return (
            <span
              key={`legend-${index}`}
              onClick={() => {
                if (onLegendClick && line) {
                  onLegendClick(line.id);
                }
              }}
              style={{
                color: entry.color,
                cursor: onLegendClick ? "pointer" : "default",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "12px",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => {
                if (onLegendClick) {
                  e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)";
                }
              }}
              onMouseLeave={(e) => {
                if (onLegendClick) {
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: "12px",
                  height: "12px",
                  backgroundColor: entry.color,
                  marginRight: "4px",
                  verticalAlign: "middle",
                }}
              />
              {entry.value}
            </span>
          );
        })}
      </div>
    );
  };

  const renderTooltip = (props: any) => {
    const { active, payload, label } = props ?? {};
    if (!active || !Array.isArray(payload) || payload.length === 0) {
      return null;
    }

    const tsRaw: number =
      typeof label === "number" ? label : typeof label === "string" ? Number(label) : NaN;
    const ts = Number.isFinite(tsRaw) ? tsRaw : null;

    const items = payload
      .filter((p: any) => p?.value != null)
      .map((p: any) => {
        const name = p.name;
        const parts = p.dataKey.split('.');
        const [formattedValue] = normalizedTooltipValue(p.value, name);
        const color = p.color;
        return { name, formattedValue, color, key: p.payload.points[parts[1]].key };
      });

    if (items.length === 0) return null;

    return (
      <Paper
        elevation={0}
        sx={{
          px: 1.25,
          py: 0.9,
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${alpha(theme.palette.text.primary, 0.16)}`,
          boxShadow: theme.shadows[6],
          minWidth: 180,
        }}
      >
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          {ts != null ? formatDateTime(ts) : "—"}
        </Typography>
        <Box sx={{ mt: 0.6, display: "flex", flexDirection: "column", gap: 0.35 }}>
          {items.map((it, idx) => (
            <Box key={idx}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "2px",
                      bgcolor: it.color,
                      flex: "0 0 auto",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.primary,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {it.name}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.primary, fontWeight: 600, flex: "0 0 auto" }}
                >
                  {it.formattedValue}
                </Typography>
              </Box>
              {(tooltipExtraContent && it.key) && tooltipExtraContent({ key: it.key })}
            </Box>
          ))}
        </Box>
      </Paper>
    );
  };

  return (
    <Box
      ref={containerRef}
      sx={{ width: "100%", height, cursor: onRangeSelect && xRange ? "crosshair" : undefined }}
      onMouseDown={handleMouseDown}
      onMouseMove={dragSelect ? handleMouseMove : undefined}
    >
      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          syncId={syncId}
          data={normalizedData}
          margin={{ top: 8, right: 8, left: 8, bottom: 4 }}
        >
          <XAxis
            dataKey="ts"
            type="number"
            domain={
              xRange ? [xRange.from.getTime(), xRange.to.getTime()] : (["auto", "auto"] as const)
            }
            tickFormatter={formatTime}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            width={60}
            tickFormatter={formatYAxisTick}
            label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: "insideLeft" } : undefined}
          />
          <Tooltip content={renderTooltip} />
          <Legend content={onLegendClick ? renderCustomLegend : undefined} />
          {series.map((line) => (
            <Line
              key={line.id}
              type="monotone"
              dataKey={'points.' + line.id + '.value'}
              name={line.label}
              stroke={line.color}
              strokeWidth={strokeWidth}
              dot={dot}
              connectNulls
              activeDot={
                {
                  onClick: (_, data: unknown) => {
                    const segment = data as { payload: { points: { [key: string]: { key?: string } }, ts: number } };
                    if (segment.payload.points[line.id]?.key) {
                      onPointClick?.({ ts: segment.payload.ts, key: segment.payload.points[line.id]?.key });
                    }
                  },
                  cursor: "pointer",
                }
              }
              isAnimationActive={false}
            />
          ))}
          <ReferenceLine
            x={hoveredRange?.from.getTime()}
            stroke={alpha(theme.palette.text.primary, 0.35)}
            strokeDasharray="4 4"
            ifOverflow="hidden"
          />
          {dragSelect && (
            <ReferenceArea
              x1={Math.min(dragSelect.startTs, dragSelect.endTs)}
              x2={Math.max(dragSelect.startTs, dragSelect.endTs)}
              fill={alpha(theme.palette.primary.main, 0.2)}
              stroke={theme.palette.primary.main}
              strokeWidth={1}
            />
          )}
          <Brush
            dataKey="ts"
            height={20}
            travellerWidth={8}
            tickFormatter={formatTime}
          />
        </LineChart>
      </ResponsiveContainer>
      {footer}
    </Box>
  );
}
